/**
 * Generischer Tab für eine Referenz-Tabelle.
 *
 * Stellt Liste, Stichtags-Filter und ein Modal für Create/Edit/Delete
 * bereit. Die Spalten und Form-Felder werden per Config übergeben, damit
 * derselbe Tab für alle acht Referenz-Tabellen funktioniert.
 */
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertCircle,
  IconEdit,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';

import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';

export type FieldKind =
  | 'text'
  | 'number'
  | 'percent'
  | 'date'
  | 'date_nullable'
  | 'bool'
  | 'select'
  | 'select_nullable';

export interface FieldConfig {
  name: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  helperText?: string;
}

export interface ColumnConfig<Row> {
  key: keyof Row | string;
  label: string;
  render?: (row: Row) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
}

export interface RefDataTabProps<Row, Form> {
  title: string;
  rows: Row[] | undefined;
  isLoading: boolean;
  error: unknown;
  columns: ColumnConfig<Row>[];
  fields: FieldConfig[];
  formInitial: () => Form;
  rowToForm: (row: Row) => Form;
  onCreate: (body: Form) => Promise<void>;
  onUpdate: (id: string, body: Form) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  withStichtag?: boolean;
  stichtag?: string | null;
  onStichtagChange?: (date: string | null) => void;
  rowKey: (row: Row) => string;
  description?: string;
}

export function RefDataTab<Row extends { id: string }, Form>({
  title,
  rows,
  isLoading,
  error,
  columns,
  fields,
  formInitial,
  rowToForm,
  onCreate,
  onUpdate,
  onDelete,
  withStichtag = true,
  stichtag = null,
  onStichtagChange,
  rowKey,
  description,
}: RefDataTabProps<Row, Form>) {
  const [opened, modal] = useDisclosure(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(formInitial());
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const openCreate = () => {
    setEditingId(null);
    setForm(formInitial());
    setFormError(null);
    modal.open();
  };

  const openEdit = (row: Row) => {
    setEditingId(row.id);
    setForm(rowToForm(row));
    setFormError(null);
    modal.open();
  };

  const handleSave = async () => {
    setBusy(true);
    setFormError(null);
    try {
      if (editingId === null) {
        await onCreate(form);
      } else {
        await onUpdate(editingId, form);
      }
      modal.close();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: string };
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object' && detail !== null) {
        setFormError(JSON.stringify(detail));
      } else if (typeof detail === 'string') {
        setFormError(detail);
      } else {
        setFormError(err.message ?? 'Unbekannter Fehler');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    await onDelete(id);
  };

  const updateField = (name: string, value: unknown) => {
    setForm((prev) => ({ ...prev, [name]: value }) as Form);
  };

  const sortedRows = useMemo(() => {
    if (!rows) return [];
    return [...rows].sort((a, b) => {
      const ka = String((a as Record<string, unknown>).gueltig_ab ?? '');
      const kb = String((b as Record<string, unknown>).gueltig_ab ?? '');
      return ka.localeCompare(kb);
    });
  }, [rows]);

  return (
    <Stack>
      <Group justify="space-between" align="flex-end">
        <Box>
          <Title order={4}>{title}</Title>
          {description ? (
            <Text c="dimmed" size="sm">
              {description}
            </Text>
          ) : null}
        </Box>
        <Group gap="sm">
          {withStichtag ? (
            <DateInput
              label="Gültig am"
              value={stichtag ? new Date(stichtag) : null}
              onChange={(d) =>
                onStichtagChange?.(d ? toISO(d as unknown as Date) : null)
              }
              clearable
              w={180}
            />
          ) : null}
          <Button leftSection={<IconPlus size={14} />} onClick={openCreate}>
            Neuer Eintrag
          </Button>
        </Group>
      </Group>

      {isLoading ? <LoadingState height={120} /> : null}
      {error ? <ErrorState error={error} /> : null}
      {rows && rows.length === 0 ? <EmptyState /> : null}

      {sortedRows.length > 0 ? (
        <Paper withBorder>
          <Table striped withRowBorders={false}>
            <Table.Thead>
              <Table.Tr>
                {columns.map((c) => (
                  <Table.Th key={String(c.key)} style={{ textAlign: c.align }}>
                    {c.label}
                  </Table.Th>
                ))}
                <Table.Th style={{ width: 96 }} aria-label="Aktionen" />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedRows.map((row) => (
                <Table.Tr key={rowKey(row)}>
                  {columns.map((c) => (
                    <Table.Td
                      key={String(c.key)}
                      style={{ textAlign: c.align }}
                    >
                      {c.render
                        ? c.render(row)
                        : String(
                            (row as Record<string, unknown>)[
                              c.key as string
                            ] ?? '—',
                          )}
                    </Table.Td>
                  ))}
                  <Table.Td>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => openEdit(row)}
                        aria-label="Bearbeiten"
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => handleDelete(rowKey(row))}
                        aria-label="Löschen"
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      ) : null}

      <Modal
        opened={opened}
        onClose={modal.close}
        title={editingId === null ? 'Neuer Eintrag' : 'Eintrag bearbeiten'}
        centered
        size="lg"
      >
        <Stack>
          {fields.map((f) =>
            renderField(f, (form as Record<string, unknown>)[f.name], (v) =>
              updateField(f.name, v),
            ),
          )}
          {formError ? (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Fehler"
            >
              {formError}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={modal.close} disabled={busy}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} loading={busy}>
              Speichern
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function renderField(
  f: FieldConfig,
  value: unknown,
  onChange: (v: unknown) => void,
) {
  if (f.kind === 'text') {
    return (
      <TextInput
        key={f.name}
        label={f.label}
        value={(value as string) ?? ''}
        onChange={(e) => onChange(e.currentTarget.value)}
        required={f.required}
        description={f.helperText}
      />
    );
  }
  if (f.kind === 'number' || f.kind === 'percent') {
    return (
      <NumberInput
        key={f.name}
        label={f.label}
        value={value === '' || value === null ? '' : Number(value)}
        onChange={(v) => onChange(typeof v === 'number' ? String(v) : '')}
        decimalScale={f.kind === 'percent' ? 4 : 2}
        step={f.kind === 'percent' ? 0.01 : 1}
        description={f.helperText}
        required={f.required}
      />
    );
  }
  if (f.kind === 'date') {
    return (
      <DateInput
        key={f.name}
        label={f.label}
        value={value ? new Date(value as string) : null}
        onChange={(d) =>
          onChange(d ? toISO(d as unknown as Date) : '')
        }
        required={f.required}
      />
    );
  }
  if (f.kind === 'date_nullable') {
    return (
      <DateInput
        key={f.name}
        label={f.label}
        value={value ? new Date(value as string) : null}
        onChange={(d) =>
          onChange(d ? toISO(d as unknown as Date) : null)
        }
        clearable
        description={f.helperText}
      />
    );
  }
  if (f.kind === 'bool') {
    return (
      <Checkbox
        key={f.name}
        label={f.label}
        checked={Boolean(value)}
        onChange={(e) => onChange(e.currentTarget.checked)}
      />
    );
  }
  if (f.kind === 'select') {
    return (
      <Select
        key={f.name}
        label={f.label}
        data={f.options ?? []}
        value={(value as string) ?? null}
        onChange={(v) => onChange(v ?? '')}
        required={f.required}
        description={f.helperText}
      />
    );
  }
  if (f.kind === 'select_nullable') {
    return (
      <Select
        key={f.name}
        label={f.label}
        data={f.options ?? []}
        value={(value as string) ?? null}
        onChange={(v) => onChange(v)}
        clearable
        description={f.helperText}
      />
    );
  }
  return null;
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
